import { put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import type { List } from 'immutable';
import type { SavableFilterTypes } from '~/ducks/machine-filter-v2/types/filter-value-types';
import type { AppState } from '~/reducers';
import { setForceReload as setForceReloadAssetListAction } from '~/domain/inventory-list/store/asset-list-reducer';
import { showError } from '~/components/containers/UnhandledErrorContainer/actions';
import {
  setFilterValues,
  getSavedFilters as getSavedFiltersAction,
} from '~/ducks/machine-filter-v2/actions';
import callRequestSaga from '~/sagas/utils/request-saga';
import MachineFiltersAPI from '~/api/machine-filters-api';
import MachineFilterV2Constants from '~/ducks/machine-filter-v2/constants';
import {
  getSearchStringFromFilters,
  parseFiltersFromSearchString,
} from '~/ducks/machine-filter-v2/utils/filter-utils';
import { redirectUser } from '~/ducks/router/actions';
import BaseException from '~/utils/fetch-wrapper/errors/BaseException';
import { removeFromSearchString } from '~/utils/query-helper/query-helper';

export function* getSavedFilters() {
  yield* callRequestSaga(MachineFiltersAPI.getSavedFilters, {
    request: MachineFilterV2Constants.GET_SAVED_FILTERS_REQUEST,
    success: MachineFilterV2Constants.GET_SAVED_FILTERS_REQUEST_SUCCESS,
    failure: MachineFilterV2Constants.GET_SAVED_FILTERS_REQUEST_ERROR,
  });
}

const activeFiltersSelector = ({ machineFilterV2 }: AppState) => machineFilterV2.getFilters();

function* updateSearchOnFilterChange() {
  const activeFilters: List<SavableFilterTypes> = yield select(activeFiltersSelector);
  const searchQuery: string = window.location.search;
  const searchStringWithoutFilters = removeFromSearchString(searchQuery, 'filters');
  let newSearchString = getSearchStringFromFilters(activeFilters);

  if (searchStringWithoutFilters) {
    newSearchString = newSearchString.concat('&', searchStringWithoutFilters);
  }

  yield put(
    redirectUser({
      path: '',
      search: newSearchString,
      pushToHistory: false,
    })
  );
}

function* parseSearchAndGetFilterDefinition(action) {
  const filters = parseFiltersFromSearchString(action.searchString);

  if (filters) {
    yield put(setFilterValues(filters));
  }
}

function* saveNewFilter({ filters, filterName, favorite }) {
  const response = yield* callRequestSaga(
    MachineFiltersAPI.saveNewFilter,
    {
      request: MachineFilterV2Constants.SAVE_NEW_FILTER_REQUEST,
      success: MachineFilterV2Constants.SAVE_NEW_FILTER_REQUEST_SUCCESS,
      failure: MachineFilterV2Constants.SAVE_NEW_FILTER_REQUEST_ERROR,
    },
    filters,
    filterName,
    favorite
  );

  if (response instanceof BaseException) {
    return;
  }

  yield put(getSavedFiltersAction());
}

function* editSavedFilter({ filterId, filters, filterName, favorite }) {
  const response = yield* callRequestSaga(
    MachineFiltersAPI.editSavedFilter,
    {
      request: MachineFilterV2Constants.EDIT_SAVED_FILTER_REQUEST,
      success: MachineFilterV2Constants.EDIT_SAVED_FILTER_REQUEST_SUCCESS,
      failure: MachineFilterV2Constants.EDIT_SAVED_FILTER_REQUEST_ERROR,
      payload: {
        filterId,
        filters,
        filterName,
        favorite,
      },
    },
    filterId,
    {
      filters,
      favorite,
      name: filterName,
    }
  );

  if (response instanceof BaseException) {
    yield put(showError(response.firstError, null, true));
  } else {
    yield put(getSavedFiltersAction());
  }
}

function* removeSavedFilter({ filterId }) {
  const response = yield* callRequestSaga(
    MachineFiltersAPI.removeSavedFilter,
    {
      request: MachineFilterV2Constants.REMOVE_SAVED_FILTER_REQUEST,
      success: MachineFilterV2Constants.REMOVE_SAVED_FILTER_REQUEST_SUCCESS,
      failure: MachineFilterV2Constants.REMOVE_SAVED_FILTER_REQUEST_ERROR,
      payload: {
        filterId,
      },
    },
    filterId
  );

  if (response instanceof BaseException) {
    yield put(showError(response.errors[0], null, true));
  } else {
    yield put(getSavedFiltersAction());
  }
}

function* setForceReloadAssetList() {
  yield put(setForceReloadAssetListAction());
}

export function* getSavedFiltersSaga() {
  yield takeLatest(MachineFilterV2Constants.GET_SAVED_FILTERS, getSavedFilters);
}

export function* setForceReloadAssetListSaga() {
  yield takeLatest(
    [
      MachineFilterV2Constants.APPLY_SAVED_FILTER,
      MachineFilterV2Constants.ADD_FILTER_VALUE,
      MachineFilterV2Constants.CLEAR_ALL_FILTER_VALUES,
      MachineFilterV2Constants.SET_FILTER_VALUES,
      MachineFilterV2Constants.REMOVE_FILTER_VALUE,
      MachineFilterV2Constants.CLEAR_FILTER_VALUES_BY_NAME,
    ],
    setForceReloadAssetList
  );
}

export function* updateSearchSaga() {
  yield takeLatest(
    [
      MachineFilterV2Constants.ADD_FILTER_VALUE,
      MachineFilterV2Constants.REMOVE_FILTER_VALUE,
      MachineFilterV2Constants.CLEAR_FILTER_VALUES_BY_NAME,
    ],
    updateSearchOnFilterChange
  );
}

export function* parseSearchAndGetFilterDefinitionSaga() {
  yield takeLatest(
    [MachineFilterV2Constants.PARSE_SEARCH_AND_GET_FILTER_DEFINITIONS],
    parseSearchAndGetFilterDefinition
  );
}

export function* saveNewFilterSaga() {
  // @ts-ignore
  yield takeLatest(MachineFilterV2Constants.SAVE_NEW_FILTER, saveNewFilter);
}

export function* editSavedFilterSaga() {
  // @ts-ignore
  yield takeEvery(MachineFilterV2Constants.EDIT_SAVED_FILTER, editSavedFilter);
}

export function* removeSavedFilterSaga() {
  yield takeEvery(
    // @ts-ignore
    MachineFilterV2Constants.REMOVE_SAVED_FILTER,
    removeSavedFilter
  );
}

export default [
  getSavedFiltersSaga,
  parseSearchAndGetFilterDefinitionSaga,
  editSavedFilterSaga,
  saveNewFilterSaga,
  removeSavedFilterSaga,
  setForceReloadAssetListSaga,
  updateSearchSaga,
];
